import React, { FC } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import classNames from 'classnames';

import toPath from 'lodash-es/toPath';
import { FormFieldTooltip } from './FormFieldTooltip';

export const deepGet = (obj: any, path: string) => {
    const segments = toPath(path);
    let val = obj;
    for (const segment of segments) {
        if (val === undefined) return val;
        val = val[segment];
    }
    return val;
};

interface TooltipIconData {
    mainIcon: string;
    closeIcon: string;
}

interface FormBooleanFieldProps {
    fieldName: string;
    label: string;
    readOnly?: boolean;
    required?: boolean;
    tooltipIconData?: TooltipIconData;
    tooltip?: string;
    renderTooltipTextAsHtml?: boolean;
    customClassName?: string;
}

export const FormBooleanField: FC<FormBooleanFieldProps> = ({
    fieldName,
    label,
    readOnly = false,
    required = false,
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml = false,
    customClassName,
}) => {
    const formikContext = useFormikContext<any>();

    const handleChange = (value: boolean) => {
        if (!formikContext.isSubmitting) {
            formikContext.setFieldValue(fieldName, value);
        }
    };

    return (
        <div
            className={classNames(
                `${
                    customClassName
                        ? customClassName + ' FormBox FormSideBySide'
                        : 'FormBox FormSideBySide'
                }`,
                {
                    FormFieldRequired: required,
                }
            )}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData && (
                    <FormFieldTooltip
                        tooltipText={tooltip}
                        tooltipMainIconSource={tooltipIconData.mainIcon}
                        tooltipCloseIconSource={tooltipIconData.closeIcon}
                        renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                    />
                )}
            </div>
            <div className="RadioButtons">
                {/* Yes Option */}
                <div
                    className="Radio"
                    role="checkbox"
                    aria-checked={!!deepGet(formikContext.values, fieldName)}
                    onClick={() => handleChange(true)}
                >
                    <input
                        id={`${fieldName}-yes`}
                        type="radio"
                        name={fieldName}
                        value="true"
                        checked={!!deepGet(formikContext.values, fieldName)}
                        readOnly
                    />
                    <label className="CustomInput" htmlFor={`${fieldName}-yes`}>
                        Yes
                    </label>
                </div>
                {/* No Option */}
                <div
                    className="Radio"
                    role="checkbox"
                    aria-checked={!deepGet(formikContext.values, fieldName)}
                    onClick={() => handleChange(false)}
                >
                    <input
                        id={`${fieldName}-no`}
                        type="radio"
                        name={fieldName}
                        value="false"
                        checked={!deepGet(formikContext.values, fieldName)}
                        readOnly
                    />
                    <label className="CustomInput" htmlFor={`${fieldName}-no`}>
                        No
                    </label>
                </div>
            </div>
            <ErrorM name={fieldName} />
        </div>
    );
};

interface ErrorMProps {
    name: string;
}

export const ErrorM: FC<ErrorMProps> = ({ name }) => (
    <ErrorMessage name={name}>{(msg) => <div className="ErrorLabel">{msg}</div>}</ErrorMessage>
);
